import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

import { Calendar } from '../types';
import { useEditGuard } from './useEditGuard';
export type { Calendar };

interface UseCalendarsOptions {
  spaceId?: string | null; // null = personal space, undefined = not filtering
}

export function useCalendars(options: UseCalendarsOptions = {}) {
  const allowEdit = useEditGuard();
  const { spaceId } = options;
  const { user } = useAuth();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarsSpaceId, setCalendarsSpaceId] = useState<string | null | undefined>(spaceId);

  const currentCalendarsSpaceIdRef = useRef<string | null | undefined>(spaceId);
  const calendarsRef = useRef<Calendar[]>([]);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    calendarsRef.current = calendars;
  }, [calendars]);

  const fetchCalendars = useCallback(async () => {
    if (!user) {
      setCalendars([]);
      setLoading(false);
      return;
    }

    try {
      let query = supabase
        .from('calendars')
        .select('*')
        .order('sort_order', { ascending: true, nullsFirst: true })
        .order('created_at', { ascending: true });

      if (spaceId === null) {
        query = query.is('space_id', null).eq('user_id', user.id);
      } else if (spaceId !== undefined) {
        query = query.eq('space_id', spaceId);
      } else {
        query = query.is('space_id', null).eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) {
        const defaults = [
          { name: 'Personal', color: 'bg-blue-500' },
          { name: 'Work', color: 'bg-purple-500' },
          { name: 'Travel', color: 'bg-emerald-500' }
        ];

        const { data: newCalendars } = await supabase
          .from('calendars')
          .insert(defaults.map(c => ({
            ...c,
            user_id: user.id,
            space_id: spaceId || null,
          })))
          .select();

        setCalendars(newCalendars || []);
        setCalendarsSpaceId(spaceId);
        currentCalendarsSpaceIdRef.current = spaceId;
      } else {
        setCalendars(data);
        setCalendarsSpaceId(spaceId);
        currentCalendarsSpaceIdRef.current = spaceId;
      }
    } catch (err) {
      console.error('Error fetching calendars:', err);
    } finally {
      setLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [user, spaceId]);

  const isDuplicateName = (name: string, excludeId?: string): boolean => {
    return calendars.some(c => c.id !== excludeId && c.name.toLowerCase() === name.toLowerCase());
  };

  const addCalendar = async (calendar: Omit<Calendar, 'id'>) => {
    if (!user) return null;
    if (!allowEdit()) return null;
    if (isDuplicateName(calendar.name)) return null;

    const { data, error } = await supabase
      .from('calendars')
      .insert([{
        ...calendar,
        user_id: user.id,
        space_id: spaceId || null,
      }])
      .select()
      .single();

    if (!error && data) {
      setCalendars([...calendars, data]);
    }
    return data;
  };

  const updateCalendar = async (id: string, updates: Partial<Calendar>) => {
    if (!user) return null;
    if (!allowEdit()) return null;
    if (updates.name && isDuplicateName(updates.name, id)) return null;

    const { data, error } = await supabase
      .from('calendars')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (!error && data) {
      setCalendars(calendars.map(c => c.id === id ? data : c));
    }
    return data;
  };

  const deleteCalendar = async (id: string) => {
    if (!user) return;
    if (!allowEdit()) return;

    const { error } = await supabase
      .from('calendars')
      .delete()
      .eq('id', id);

    if (!error) {
      setCalendars(calendars.filter(c => c.id !== id));
    }
  };

  const reorderCalendars = async (fromIndex: number, toIndex: number) => {
    if (!allowEdit()) return;
    const reordered = [...calendars];
    [reordered[fromIndex], reordered[toIndex]] = [reordered[toIndex], reordered[fromIndex]];
    setCalendars(reordered);
    await Promise.all(
      reordered.map((c, i) =>
        supabase.from('calendars').update({ sort_order: i }).eq('id', c.id)
      )
    );
  };

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  return {
    calendars,
    calendarsSpaceId,
    loading,
    addCalendar,
    updateCalendar,
    deleteCalendar,
    isDuplicateName,
    refetch: fetchCalendars,
    reorderCalendars,
  };
}
